'use client';

import React, { useState } from 'react';
import Modal from '@/components/Modal';
import { useGetEmiInfo } from '@/hooks/ordersHooks';
import Image from 'next/image';
import { Box, Skeleton, Table } from '@mantine/core';
import { numberFormat } from '@/utils/helper';
import { BasicRadio } from '@/components/BasicRadio';
import { useRouter, usePathname } from 'next/navigation';
import { useSearchParams } from '@/hooks/useCustomSearchParams';
import { Checkbox } from '@/components/HookForm';
import { PageHeading } from '@/components/PageHeading';
import { Transition } from '@headlessui/react';
import { useEmiStore } from '@/store/emi';
import { EmiInfo } from '@/interfaces/orders';

export const EMISection = ({
  productPrice,
  setEmiInformation,
  emiInformation,
  onConfirmModal,
  showConfirmBtn = true,
}: {
  productPrice: number;
  setEmiInformation?: Function;
  emiInformation?: {
    bankId: string;
    bankEmiId: string;
  };
  onConfirmModal?: Function;
  showConfirmBtn?: boolean;
}) => {
  const { data: emiInfoData, isLoading } = useGetEmiInfo();
  const [selectedBank, setSelectedBank] = useState<number>(0);
  const pathname = usePathname();
  const [selectEmiPlan, setSelectEmiPlan] = useState<{
    bankId: string;
    bankEmiId: string;
  }>({
    bankId: emiInformation?.bankId || '',
    bankEmiId: emiInformation?.bankEmiId || '',
  });

  const confirmEmiSelection = () => {
    if (emiInfoData?.[selectedBank]?.Id && selectEmiPlan?.bankEmiId) {
      setEmiInformation?.({
        bankId: emiInfoData?.[selectedBank]?.Id,
        bankEmiId: selectEmiPlan?.bankEmiId,
      });
      if (onConfirmModal) {
        onConfirmModal();
      }
    }
  };

  const rowEmiInfo =
    emiInfoData?.[selectedBank]?.EmiInfos && emiInfoData?.[selectedBank]?.EmiInfos?.length > 0 ? (
      emiInfoData[selectedBank].EmiInfos.map((element: EmiInfo) => {
        return (
          <Table.Tr key={element?.Id}>
            <Table.Td className="flex items-center justify-center">
              {pathname?.split('/')[2] === 'payment' && (
                <BasicRadio
                  name={''}
                  value={selectEmiPlan?.bankEmiId as string}
                  label={undefined}
                  selectedValues={[selectEmiPlan?.bankEmiId as string]}
                  onChange={() => {
                    setSelectEmiPlan?.({
                      bankId: emiInfoData?.[selectedBank]?.Id,
                      bankEmiId: element?.Id,
                    });
                  }}
                  isChecked={element?.Id === selectEmiPlan?.bankEmiId}
                ></BasicRadio>
              )}
              {element?.Tenure} Months
            </Table.Td>
            <Table.Td>{element?.InterestRate} %</Table.Td>
            <Table.Td>{numberFormat(Math.round((element?.InterestRate / 100) * productPrice))}</Table.Td>
            <Table.Td>{numberFormat(Math.round((productPrice + (element?.InterestRate / 100) * productPrice) / element?.Tenure))}</Table.Td>
            <Table.Td>{numberFormat(Math.round(productPrice + (element?.InterestRate / 100) * productPrice))}</Table.Td>
          </Table.Tr>
        );
      })
    ) : (
      <Table.Tr>
        <Table.Td colSpan={5} className="text-center">
          No data found
        </Table.Td>
      </Table.Tr>
    );

  if (isLoading || !rowEmiInfo)
    return (
      <div className="mb-5 mt-2 h-full w-full border border-gray-400 bg-white p-3">
        {Array(15)
          .fill(0)
          .map((_, index) => (
            <Box
              key={index}
              style={{
                width: '100%',
                marginTop: index !== 0 ? '0.5rem' : '',
              }}
            >
              <Box
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Skeleton animate={true} className="content-skeleton" style={{ width: '100%', height: '1rem' }} />
              </Box>
            </Box>
          ))}
      </div>
    );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {emiInfoData?.map((info, index) => (
          <div
            key={info.Id}
            className={`h-[80px] w-[80px] cursor-pointer rounded-lg border border-gray-300 p-2 ${
              index === selectedBank ? 'bg-red-600 text-white' : 'hover:bg-red-600'
            } group`}
            onClick={() => setSelectedBank(index)}
          >
            <div className="relative mx-auto h-[40px] w-[60px]">
              <Image
                src={info?.PictureUrl || ''}
                alt={info.Name}
                quality={100}
                layout="fill"
                style={{ objectFit: 'contain' }}
                className="rounded-md p-2"
              />
            </div>
            <div
              title={info.Name}
              className={`text-center text-sm leading-tight ${
                index === selectedBank ? 'text-white' : 'text-gray-500'
              } line-clamp-2 overflow-hidden text-ellipsis group-hover:text-white`}
            >
              {info.Name}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 w-full shadow-[0px_2px_18px_0px_rgba(150,150,150,0.2)]">
        <div className="flex items-center justify-between border-b p-5">
          <div className="text-xl font-bold">EMI available with the following plans</div>
        </div>

        {emiInfoData && (
          <div className="relative overflow-x-auto text-center">
            <div className="max-h-[160px] overflow-y-auto">
              <Table
                withColumnBorders
                styles={{
                  table: { fontSize: '12px' },
                  th: { fontSize: '14px', textAlign: 'center', color: 'rgb(73, 80, 87)' },
                }}
              >
                <Table.Thead className="sticky top-0 z-[200] bg-white text-center">
                  <Table.Tr>
                    <Table.Th className="whitespace-nowrap">EMI Tenure</Table.Th>
                    <Table.Th className="whitespace-nowrap">Interest Rate</Table.Th>
                    <Table.Th className="whitespace-nowrap">Interest Payable</Table.Th>
                    <Table.Th className="whitespace-nowrap">EMI Amount</Table.Th>
                    <Table.Th className="whitespace-nowrap">Total Payment</Table.Th>
                  </Table.Tr>
                </Table.Thead>

                <Table.Tbody className="overflow-y-auto text-center">{rowEmiInfo}</Table.Tbody>
              </Table>
            </div>
          </div>
        )}
        {!showConfirmBtn && (
          <div className="flex items-center justify-between border-t p-5">
            <p className="text-lg font-medium">* You can select your EMI plan while confirming order on PAYMENT page.</p>
          </div>
        )}
      </div>
      {showConfirmBtn && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => {
              confirmEmiSelection();
            }}
            disabled={selectEmiPlan?.bankEmiId === ''}
            className={`${
              selectEmiPlan?.bankEmiId === '' && 'cursor-not-allowed'
            } flex items-center justify-center rounded-lg border border-gray-300 text-xl transition duration-150 ease-linear hover:bg-black hover:text-white`}
          >
            Confirm
          </button>
        </div>
      )}
    </div>
  );
};

export const EMISectionComponent = ({
  productPrice,
  setEmiInformation,
  emiInformation,
}: {
  productPrice: number;
  setEmiInformation?: Function;
  emiInformation?: {
    bankId: string;
    bankEmiId: string;
  };
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const showEMISection = useEmiStore((state) => state.showEMISection);
  const setShowEMISection = useEmiStore((state) => state.setShowEMISection);
  const previousPath = useEmiStore((state) => state.previousPath);
  const setPreviousPath = useEmiStore((state) => state.setPreviousPath);
  const [showEMIModal, setShowEMIModal] = useState<boolean>(false);

  const onCheckboxClick = () => {
    const addressShippingId = searchParams?.get('addressShippingId') || '';
    if (pathname?.split('/')[3] === 'cash-on-delivery') {
      router.push('/checkout/payment/online-payment' + '?addressShippingId=' + addressShippingId);
      setPreviousPath('cash-on-delivery');
      setShowEMISection(true);
    } else if (pathname?.split('/')[3] === 'online-payment' && previousPath === 'cash-on-delivery') {
      router.push('/checkout/payment/cash-on-delivery' + '?addressShippingId=' + addressShippingId);
      setPreviousPath('');
      setShowEMISection(false);
      setEmiInformation?.({
        bankId: '',
        bankEmiId: '',
      });
    } else {
      if (showEMISection) {
        setShowEMISection(false);
        setEmiInformation?.({
          bankId: '',
          bankEmiId: '',
        });
      } else {
        setShowEMISection(true);
      }
    }
  };

  const onConfirmModal = () => {
    setShowEMIModal(false);
  };

  return (
    <>
      <div className="mt-[6px] flex items-center">
        <Checkbox checked={showEMISection} onClick={() => onCheckboxClick()}></Checkbox>
        <PageHeading className="text-lg max-sm:text-lg">I want to avail EMI</PageHeading>
      </div>

      <Transition
        show={showEMISection}
        enter="transition-all duration-300"
        enterFrom="max-h-[0px] opacity-0"
        enterTo="max-h-full opacity-100"
        leave="transition-all duration-300"
        leaveFrom="max-h-full opacity-100"
        leaveTo="max-h-[0px] opacity-0"
      >
        <div
          onClick={() => {
            setShowEMIModal(true);
          }}
          className="mt-4 w-full cursor-pointer items-center justify-center border-2 border-black p-3 text-lg font-semibold transition duration-150 ease-linear hover:bg-black hover:text-white"
        >
          {emiInformation?.bankEmiId && emiInformation?.bankId ? 'Click to Change EMI Plan' : 'Click to Select EMI Plan'}
        </div>
      </Transition>

      {showEMISection && (
        <PageHeading className="pl-2 text-lg max-sm:text-lg">
          <p className="text-md font-medium">*Uncheck the box to remove EMI selection.</p>
        </PageHeading>
      )}

      <Modal
        heading="EMI Options"
        open={showEMIModal}
        closeModal={() => {
          setShowEMIModal(false);
        }}
        className="w-full !p-1 md:w-[80rem]"
      >
        <EMISection
          productPrice={productPrice}
          emiInformation={emiInformation}
          setEmiInformation={setEmiInformation}
          onConfirmModal={onConfirmModal}
        ></EMISection>
      </Modal>
    </>
  );
};

export default EMISectionComponent;
